import {
    EditorTable,
    EntrancePosition,
    TableShape,
    WallSide,
} from 'components/tableMaps/CreateTableMapWizard/context';
import { TableMapDetails, TableMapListItem } from './reducer';

export interface MapEntrance {
    position: { x: number; y: number };
}

export interface MapTableDTO {
    id: string;
    mapId: string;
    name: string;
    updateDate: string | null;
    publishedTimestamp: number;
    totalSeats: number;
    shape: 'Round' | 'Square' | 'Rectangle';
    position: { x: number; y: number };
    rotation?: number;
    isActive: boolean;
    qrCode: string | null;
}

export interface TablesMapDTO {
    id: string;
    name: string;
    updateDate: string | null;
    isActive: boolean;
    entrance: MapEntrance | null;
    tables: MapTableDTO[];
}

export interface TablesMapModel {
    name: string;
    isActive: boolean;
    entrance: MapEntrance | null;
}

export interface MapTableModel {
    name: string;
    totalSeats: number;
    shape: 'Round' | 'Square' | 'Rectangle';
    position: { x: number; y: number };
    rotation: number;
    isActive: boolean;
    qrCode: string | null;
}

export interface QRCodeDTO {
    id: string;
    name: string;
    service: string;
    url: string;
    area: string;
    isUsed: boolean;
}

const SHAPE_TO_BACKEND: Record<TableShape, 'Round' | 'Square' | 'Rectangle'> = {
    round: 'Round',
    square: 'Square',
    rectangle: 'Rectangle',
};

const SHAPE_FROM_BACKEND: Record<'Round' | 'Square' | 'Rectangle', TableShape> = {
    Round: 'round',
    Square: 'square',
    Rectangle: 'rectangle',
};

const SHAPE_DEFAULTS: Record<TableShape, { width: number; height: number }> = {
    round: { width: 0.06, height: 0.1 },
    square: { width: 0.06, height: 0.1 },
    rectangle: { width: 0.12, height: 0.1 },
};

export function deriveMapStatus(isActive: boolean): 'Active' | 'Inactive' {
    return isActive ? 'Active' : 'Inactive';
}

/** Latest change of the map itself or of any of its tables. */
export function lastUpdatedIso(dto: TablesMapDTO): string | null {
    const stamps = [dto.updateDate, ...(dto.tables ?? []).map((t) => t.updateDate)]
        .filter((d): d is string => !!d)
        .map((d) => new Date(d).getTime())
        .filter((ms) => !Number.isNaN(ms));

    if (stamps.length === 0) return null;
    return new Date(Math.max(...stamps)).toISOString();
}

export function entranceToMapEntrance(entrance: EntrancePosition | null): MapEntrance | null {
    if (!entrance) return null;
    const { wall, position } = entrance;
    switch (wall) {
        case 'top':
            return { position: { x: position, y: 0 } };
        case 'bottom':
            return { position: { x: position, y: 1 } };
        case 'left':
            return { position: { x: 0, y: position } };
        case 'right':
            return { position: { x: 1, y: position } };
    }
}

export function mapEntranceToEntrance(e: MapEntrance | null): EntrancePosition | null {
    if (!e || !e.position) return null;
    const { x, y } = e.position;
    const distLeft = x;
    const distRight = 1 - x;
    const distTop = y;
    const distBottom = 1 - y;

    const min = Math.min(distLeft, distRight, distTop, distBottom);
    let wall: WallSide;
    let position: number;

    if (min === distTop) {
        wall = 'top';
        position = x;
    } else if (min === distBottom) {
        wall = 'bottom';
        position = x;
    } else if (min === distLeft) {
        wall = 'left';
        position = y;
    } else {
        wall = 'right';
        position = y;
    }

    return { wall, position };
}

export function mapTableDtoToEditorTable(dto: MapTableDTO): EditorTable {
    const shape = SHAPE_FROM_BACKEND[dto.shape];
    const { width, height } = SHAPE_DEFAULTS[shape];
    return {
        id: dto.id,
        label: dto.name,
        shape,
        seats: dto.totalSeats,
        x: dto.position.x,
        y: dto.position.y,
        width,
        height,
        rotation: dto.rotation ?? 0,
        qrCode: dto.qrCode || null,
        isActive: dto.isActive,
    };
}

export function editorTableToMapTableModel(t: EditorTable): MapTableModel {
    return {
        name: t.label,
        totalSeats: t.seats,
        shape: SHAPE_TO_BACKEND[t.shape],
        position: { x: t.x, y: t.y },
        rotation: t.rotation,
        isActive: t.isActive,
        qrCode: t.qrCode || null,
    };
}

export function tablesMapDtoToListItem(dto: TablesMapDTO): TableMapListItem {
    const tables = dto.tables ?? [];
    return {
        id: dto.id,
        name: dto.name,
        tablesCount: tables.length,
        totalSeats: tables.reduce((sum, t) => sum + t.totalSeats, 0),
        status: deriveMapStatus(dto.isActive),
        updatedAt: lastUpdatedIso(dto),
    };
}

export function tablesMapDtoToDetails(dto: TablesMapDTO): TableMapDetails {
    const dtoTables = dto.tables ?? [];
    const tables = dtoTables.map(mapTableDtoToEditorTable);
    return {
        id: dto.id,
        name: dto.name,
        isActive: dto.isActive,
        gridEnabled: true,
        entrance: mapEntranceToEntrance(dto.entrance),
        tables,
        publishedAt: lastUpdatedIso(dto),
    };
}
